import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";

const GsaCallForSolutionsBounty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedChallenge, setSelectedChallenge] = useState<any>(null);
  const [bountyAmount, setBountyAmount] = useState("5000");
  const [timeline, setTimeline] = useState("30");
  const [requirements, setRequirements] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    if (location.state?.selectedChallenge) {
      setSelectedChallenge(location.state.selectedChallenge);
      // Pre-populate requirements based on the challenge
      setRequirements(`Solution must address: ${location.state.selectedChallenge.challenge}\n\nRequired deliverables:\n- Detailed implementation plan\n- ROI analysis\n- Compliance with federal regulations\n- Pilot program proposal\n\nAcceptance criteria:\n- Must demonstrate measurable improvement\n- Compatible with existing GSA systems\n- Scalable across multiple federal facilities`);
    }
  }, [location]);

  const handleSubmit = () => {
    if (!acceptTerms) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms and conditions to proceed.",
        variant: "destructive"
      });
      return;
    }

    // Show success toast
    toast({
      title: "GSA Call for Solutions Published Successfully",
      description: "Your challenge has been posted to the GSA knowledge network and will appear in your mySPAI profile.",
    });
    
    // Navigate to creator rewards with the solution request data
    navigate("/demo-creator-rewards", { 
      state: { 
        newCall: {
          title: selectedChallenge?.challenge?.substring(0, 50) + "..." || "GSA Solution Challenge",
          area: "GSA Federal Buildings",
          type: "Demo GSA Solutions Bounty",
          date: new Date().toLocaleDateString(),
          bountyAmount,
          timeline,
          requirements,
          supportingKBs: selectedChallenge?.supportingKBs || []
        }
      }
    });
  };

  if (!selectedChallenge) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">No challenge selected. Please go back and select a challenge.</p>
            <Button 
              onClick={() => navigate("/gsa-call-for-solutions")}
              className="w-full mt-4"
            >
              Back to Challenges
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">Create GSA Solutions Bounty</h1>
        </div>

        {/* Selected Challenge Summary */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-xl text-blue-800">Selected Challenge</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-blue-900 font-medium mb-4">
              {selectedChallenge.challenge}
            </p>
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-2">Supporting Knowledge Bases:</p>
              <div className="flex flex-wrap gap-2">
                {selectedChallenge.supportingKBs.map((kb: string, index: number) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-blue-200 text-blue-800 text-sm rounded-full"
                  >
                    {kb}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bounty Details Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Bounty Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="bountyAmount" className="text-base font-medium">Token Reward Amount</Label>
                <Input 
                  id="bountyAmount"
                  type="number"
                  value={bountyAmount}
                  onChange={(e) => setBountyAmount(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Recommended: 5,000-25,000 tokens for GSA challenges</p>
              </div>
              
              <div>
                <Label htmlFor="timeline" className="text-base font-medium">Timeline (Days)</Label>
                <Input 
                  id="timeline"
                  type="number"
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Typical GSA project timeline: 30-90 days</p>
              </div>
            </div>

            <div>
              <Label htmlFor="requirements" className="text-base font-medium">Detailed Requirements</Label>
              <Textarea
                id="requirements"
                value={requirements}
                onChange={(e) => setRequirements(e.target.value)}
                className="mt-1 min-h-32"
                placeholder="Specify requirements, deliverables, and acceptance criteria..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Federal Compliance Notice */}
        <Card className="mb-8 border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Federal Acquisition Compliance</h3>
            <p className="text-yellow-700 text-sm">
              This demo bounty follows GSA acquisition guidelines and federal procurement regulations. 
              All solutions must comply with FAR requirements, security standards, and accessibility guidelines (Section 508).
            </p>
          </CardContent>
        </Card>

        {/* Terms and Conditions */}
        <div className="flex items-start space-x-2 mb-8">
          <Checkbox 
            id="terms" 
            checked={acceptTerms}
            onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
          />
          <div>
            <Label htmlFor="terms" className="cursor-pointer text-sm">
              I confirm this challenge aligns with GSA priorities and federal regulations
            </Label>
            <p className="text-gray-500 text-xs mt-1">
              By submitting, you confirm this challenge addresses real GSA needs and complies with federal 
              acquisition guidelines. Solutions will be evaluated by GSA domain experts.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-call-for-solutions")}
            className="border-gray-300"
          >
            Back to Challenges
          </Button>
          
          <Button 
            onClick={handleSubmit}
            disabled={!acceptTerms}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            Publish GSA Solutions Bounty
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaCallForSolutionsBounty;